import { Center, Flex } from '@chakra-ui/react';
import { PRIVATE_LINK_LOADED } from 'constants/mixpanel';
import fscreen from 'fscreen';
import { useOnScreenshot } from 'hooks/useOnScreenshot';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import E2ClientProvider from 'modules/E2ClientProvider/E2ClientProvider';
import { onTrackScreenshot } from 'modules/Editor/Editor.helpers';
import { store, useAppSelector } from 'modules/Editor/store';
import {
  selectInfographHeightPx,
  selectInfographTitle,
  selectInfographWidthPx,
  selectPageBackgroundString,
} from 'modules/Editor/store/infographSelector';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { getScaleHeight } from 'modules/InfographLoader.helpers';
import { Head as CommonHead } from 'modules/common/components/Head';
import { Page } from 'modules/common/components/Page';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { isTest } from 'utils/environment';
import { ReadOnlyStructureTreeListWidgetRenderer } from 'widgets/WidgetRenderer';

import { InfographLoader } from '../InfographLoader';
import { KeyboardShortCut } from './components/KeyboardShortcut';
import PrivateLinkNavbar from './components/PrivateLinkNavbar';
import PrivateLinkViewFooter from './components/PrivateLinkViewFooter';

const InfographPrivateLinkViewLoader = (): ReactElement => {
  const widthPx = useAppSelector(selectInfographWidthPx);
  const heightPx = useAppSelector(selectInfographHeightPx);
  const activePageId = useAppSelector(selectActivePage);
  const pageBackground = useAppSelector(selectPageBackgroundString(activePageId));

  const [isFullScreen, setIsFullScreen] = useState(false);

  const privateLinkViewScale = window.innerWidth > widthPx ? getScaleHeight(heightPx) : window.innerWidth / widthPx;
  const fullScreenScale = Math.min(window.innerWidth / widthPx, window.innerHeight / heightPx);
  const pageScale = isFullScreen ? fullScreenScale : privateLinkViewScale;

  const infographRef = useRef<HTMLDivElement>(null);
  const hasPageLoaded = useRef<boolean>(false);
  const { t } = useTranslation('common_navbar');
  const infographContainerAriaLabel = t('infographContainerAriaLabel');

  const enterFullScreen = () => {
    if (fscreen.fullscreenElement === null && infographRef.current) {
      fscreen.requestFullscreen(infographRef.current);
    }
    // fscreen.fullscreenElement won't be available during test, bypass the logic related to fscreen lib when testing
    if (isTest) setIsFullScreen(true);
  };

  const exitFullScreen = () => {
    if (!!fscreen.fullscreenElement) fscreen.exitFullscreen();
  };

  const screenChangeHandler = () => {
    if (!!fscreen.fullscreenElement && !isFullScreen) {
      setIsFullScreen(true);
    } else if (!fscreen.fullscreenElement && isFullScreen) {
      setIsFullScreen(false);
    }
  };

  fscreen.addEventListener('fullscreenchange', screenChangeHandler, false);

  useOnScreenshot(onTrackScreenshot);

  useEffect(() => {
    if (!hasPageLoaded.current) {
      // track event page load for private links
      Mixpanel.track(PRIVATE_LINK_LOADED.eventName);
      // send event tracking only once after page loads
      hasPageLoaded.current = true;
    }
  }, [hasPageLoaded]);

  return (
    <Flex
      w={'100%'}
      minH={'100vh'}
      pt={75}
      top={0}
      position={'absolute'}
      bgColor={'privateLinkView.wrapperBg'}
      justifyContent={'center'}
      alignItems={'center'}
    >
      <E2ClientProvider>
        <InfographLoader isEditor={false}>
          <Center flexDir={'column'} ref={infographRef} aria-label={infographContainerAriaLabel}>
            <Page
              testId={activePageId}
              className='page-container'
              bg={pageBackground}
              width={widthPx}
              height={heightPx}
              zoom={pageScale}
              borderRadius='none'
              boxShadow='none'
              overflow='hidden'
            >
              <ReadOnlyStructureTreeListWidgetRenderer pageId={activePageId} />
            </Page>
            {!isFullScreen && <PrivateLinkViewFooter />}
            <PrivateLinkNavbar
              isFullScreen={isFullScreen}
              enterFullScreen={enterFullScreen}
              exitFullScreen={exitFullScreen}
            />
          </Center>
          <KeyboardShortCut />
        </InfographLoader>
      </E2ClientProvider>
    </Flex>
  );
};

const Head = () => {
  const infographTitle = useAppSelector(selectInfographTitle);
  const { firstName, lastName } = window?.VenngageE2?.designOwner || { firstName: '', lastName: '' };

  return <CommonHead title={`${infographTitle} - by ${firstName} ${lastName} [Infographic]`} />;
};

const PrivateLinkView = (): ReactElement => {
  return (
    <Provider store={store}>
      <Head />
      <InfographPrivateLinkViewLoader />
    </Provider>
  );
};

export default PrivateLinkView;
